import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown, Phone, Mail, MapPin, Search,
  ShoppingBag, Zap, Battery, Users, Home, BookOpen,
  Briefcase, DollarSign, MessageSquare, Globe, ArrowRight
} from 'lucide-react';

const HeaderModern: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Accueil', path: '/', icon: <Home size={18} /> },
    {
      name: 'Produits',
      path: '/products',
      icon: <ShoppingBag size={18} />,
      submenu: [
        { name: 'Terra', path: '/products/terra', badge: 'Populaire' },
        { name: 'Sunu', path: '/products/sunu', badge: 'Premium' },
        { name: 'Teranga', path: '/products/teranga', badge: 'Économique' },
        { name: 'Teranga+', path: '/products/teranga-plus', badge: 'Prévente' },
        { name: 'Tous les produits', path: '/products' }
      ]
    },
    {
      name: 'Services',
      path: '/services',
      icon: <Zap size={18} />,
      submenu: [
        { name: 'Stations Proxy Power', path: '/proxy-power' },
        { name: 'Carte des stations', path: '/stations' },
        { name: 'Pay As You Go', path: '/pay-as-you-go' },
        { name: 'Formations', path: '/training' }
      ]
    },
    { name: 'À propos', path: '/about', icon: <Users size={18} /> },
    { name: 'Blog', path: '/blog', icon: <BookOpen size={18} /> },
    { name: 'Investir', path: '/invest', icon: <DollarSign size={18} />, badge: 'Nouveau' }
  ];

  return (
    <header className={`fixed w-full z-50 top-0 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-white'
    }`}>
      {/* Top Bar - Mobile optimized */}
      <div className="bg-gradient-to-r from-primary-900 to-primary-800 text-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-2 text-xs sm:text-sm">
            {/* Contact Info */}
            <div className="hidden md:flex items-center gap-4 lg:gap-6">
              <a href="tel:+221771234567" className="flex items-center gap-1 hover:text-accent-300 transition-colors">
                <Phone size={14} />
                <span>+221 77 123 45 67</span>
              </a>
              <a href="mailto:hello@proxyemobility.com" className="flex items-center gap-1 hover:text-accent-300 transition-colors">
                <Mail size={14} />
                <span className="hidden lg:inline">hello@proxyemobility.com</span>
              </a>
            </div>

            {/* Quick Links */}
            <div className="flex items-center gap-3 sm:gap-4 ml-auto">
              <Link to="/stations" className="flex items-center gap-1 hover:text-accent-300 transition-colors">
                <MapPin size={14} />
                <span className="hidden sm:inline">Trouver une station</span>
              </Link>
              <Link to="/contact" className="flex items-center gap-1 hover:text-accent-300 transition-colors">
                <MessageSquare size={14} />
                <span className="hidden sm:inline">Support</span>
              </Link>
              <button className="flex items-center gap-1 hover:text-accent-300 transition-colors">
                <Globe size={14} />
                <span className="hidden sm:inline">FR</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="relative">
              <img
                src="/proxy-logo.png"
                alt="Proxy Emobility"
                className="h-8 sm:h-10 md:h-12 transition-transform group-hover:scale-105"
              />
              <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary-600 group-hover:w-full transition-all duration-300"></div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-2">
            {navItems.map((item, index) => (
              <div key={index} className="relative group">
                {item.submenu ? (
                  <>
                    <button className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-primary-600 font-medium transition-colors rounded-lg hover:bg-primary-50 group">
                      {item.icon}
                      <span>{item.name}</span>
                      <ChevronDown size={16} className="group-hover:rotate-180 transition-transform" />
                      {item.badge && (
                        <span className="absolute -top-1 -right-1 bg-accent-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                          {item.badge}
                        </span>
                      )}
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border border-gray-100 transform origin-top scale-95 group-hover:scale-100">
                      <div className="p-2">
                        {item.submenu.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className="flex items-center justify-between px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all group/item"
                          >
                            <span className="font-medium">{subItem.name}</span>
                            {subItem.badge && (
                              <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                                {subItem.badge}
                              </span>
                            )}
                            <ArrowRight size={14} className="opacity-0 group-hover/item:opacity-100 transition-opacity" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 font-medium transition-all rounded-lg relative ${
                      location.pathname === item.path
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                    }`}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                    {item.badge && (
                      <span className="bg-accent-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                        {item.badge}
                      </span>
                    )}
                    {location.pathname === item.path && (
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary-600 rounded-t-full"></div>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="hidden sm:flex items-center justify-center w-10 h-10 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-all"
              aria-label="Rechercher"
            >
              <Search size={20} />
            </button>

            {/* CTA Button */}
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all"
            >
              <MessageSquare size={18} />
              <span className="hidden lg:inline">Nous contacter</span>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden flex items-center justify-center w-10 h-10 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-all"
              aria-label="Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed inset-0 top-[104px] sm:top-[112px] bg-white transform transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } overflow-y-auto`}>
        <div className="container mx-auto px-4 py-6">
          <div className="space-y-2">
            {navItems.map((item, index) => (
              <div key={index}>
                {item.submenu ? (
                  <div className="border-b border-gray-100 pb-2 mb-2">
                    <div className="flex items-center gap-2 px-4 py-3 text-gray-900 font-semibold">
                      {item.icon}
                      <span>{item.name}</span>
                    </div>
                    <div className="pl-6 space-y-1">
                      {item.submenu.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.path}
                          className="flex items-center justify-between px-4 py-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
                        >
                          <span>{subItem.name}</span>
                          {subItem.badge && (
                            <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                              {subItem.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      location.pathname === item.path
                        ? 'bg-primary-50 text-primary-600 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {item.icon}
                    <span>{item.name}</span>
                    {item.badge && (
                      <span className="ml-auto bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile CTA */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-semibold rounded-full shadow-lg"
            >
              <MessageSquare size={20} />
              <span>Nous contacter</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 top-[104px] sm:top-[112px] bg-black/50 backdrop-blur-sm z-40" onClick={() => setSearchOpen(false)}>
          <div className="container mx-auto px-4 pt-8" onClick={e => e.stopPropagation()}>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un produit, une station..."
                  className="w-full pl-12 pr-4 py-4 bg-white rounded-2xl shadow-2xl text-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderModern;
